/**
 * Firewood Fulfillment — Reston Farm Garden Market
 *
 * Forces an explicit Pickup vs Local Delivery choice on the firewood PDP.
 * Delivery is ZIP-gated; the ZIP resolves a distance band, the band resolves
 * a variant of the "Firewood Local Delivery" product, and that variant is
 * added to the cart after the main product add succeeds.
 *
 * Theme integration mirrors assets/product-adder.js (Horizon):
 *   - ATC button:   [ref="addToCartButton"] inside the section
 *   - Form:         product-form-component form
 *   - Cart add:     'cart:update' dispatched on product-form-component
 *   - Variant swap: 'variant:update' on the section
 *
 * Deliberately standalone: does not touch ProductAdder, which drives the
 * bakery pickup scheduler.
 */
window.RFFirewoodFulfillment = (function () {
  'use strict';

  var root = null;
  var section = null;
  var zipMap = {};
  var bandVariants = {};
  var cfg = {};

  var state = {
    method: null,          /* 'pickup' | 'delivery' */
    zip: null,
    band: null,
    deliveryVariantId: null,
    deliveryPrice: null,
    address: '',
    date: null,            /* { iso, display } */
    variantAvailable: true
  };

  /* ---------- init ---------- */

  function init() {
    root = document.querySelector('[data-rf-fulfillment]');
    if (!root) return;
    section = root.closest('.shopify-section');

    zipMap = readJson('[data-rf-zip-map]') || {};
    bandVariants = readJson('[data-rf-delivery-variants]') || {};
    cfg = {
      leadDays: parseInt(root.dataset.leadDays || '1', 10),
      lookahead: parseInt(root.dataset.lookahead || '14', 10),
      deliveryDays: (root.dataset.deliveryDays || '2,3,4,5,6').split(',').map(Number),
      hideStackingOn: (root.dataset.hideStackingOn || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean)
    };

    var atc = section ? section.querySelector('[ref="addToCartButton"]') : null;
    state.variantAvailable = atc ? !atc.disabled : true;

    bindMethodTiles();
    bindZip();
    buildDateTiles();
    interceptSubmit();
    bindCartAdd();
    applyStackingVisibility();

    if (section) {
      section.addEventListener('variant:update', function (e) {
        state.variantAvailable = Boolean(e.detail && e.detail.resource && e.detail.resource.available);
        requestAnimationFrame(function () {
          applyStackingVisibility();
          syncProperties();
          refresh();
        });
      });
    }

    refresh();
  }

  function readJson(sel) {
    var el = root.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ---------- method selection ---------- */

  function bindMethodTiles() {
    root.querySelectorAll('[data-rf-method]').forEach(function (tile) {
      tile.addEventListener('click', function () { selectMethod(tile.dataset.rfMethod); });
      tile.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); selectMethod(tile.dataset.rfMethod); }
      });
    });
  }

  function selectMethod(method) {
    state.method = method;

    root.querySelectorAll('[data-rf-method]').forEach(function (t) {
      var on = t.dataset.rfMethod === method;
      t.classList.toggle('rf-ff-tile--selected', on);
      t.setAttribute('aria-checked', String(on));
    });

    var panel = root.querySelector('[data-rf-delivery-panel]');
    if (panel) panel.hidden = method !== 'delivery';

    if (method === 'pickup') {
      /* Clear any delivery state so a switched mind cannot leave a fee behind. */
      state.zip = null; state.band = null;
      state.deliveryVariantId = null; state.deliveryPrice = null;
      state.address = ''; state.date = null;
      var zi = root.querySelector('[data-rf-zip]'); if (zi) zi.value = '';
      var ai = root.querySelector('[data-rf-address]'); if (ai) ai.value = '';
      clearZipResult();
      clearDateSelection();
    }

    syncProperties();
    refresh();
  }

  /* ---------- ZIP checking ---------- */

  function bindZip() {
    var input = root.querySelector('[data-rf-zip]');
    var btn = root.querySelector('[data-rf-zip-check]');
    if (!input) return;

    input.addEventListener('input', function () {
      input.value = input.value.replace(/\D/g, '').slice(0, 5);
      if (input.value.length < 5) { state.band = null; state.zip = null; clearZipResult(); refresh(); }
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); checkZip(); }
    });
    input.addEventListener('blur', function () { if (input.value.length === 5) checkZip(); });
    if (btn) btn.addEventListener('click', function (e) { e.preventDefault(); checkZip(); });
  }

  function checkZip() {
    var input = root.querySelector('[data-rf-zip]');
    if (!input) return;
    var zip = (input.value || '').trim();
    if (zip.length !== 5) { showZip(false, 'Enter a 5-digit ZIP code.'); return; }

    var band = zipMap[zip];
    if (!band || !bandVariants[band]) {
      state.zip = zip; state.band = null;
      state.deliveryVariantId = null; state.deliveryPrice = null;
      showZip(false, zip + ' is outside our delivery area. You can still choose pickup at the market.');
      syncProperties(); refresh();
      return;
    }

    state.zip = zip;
    state.band = band;
    state.deliveryVariantId = bandVariants[band].id;
    state.deliveryPrice = bandVariants[band].price;

    showZip(true, 'Delivers to ' + zip + ' — ' + band.replace(/-/, '–') + ' — ' + money(state.deliveryPrice));
    syncProperties();
    refresh();
  }

  function showZip(ok, msg) {
    var el = root.querySelector('[data-rf-zip-result]');
    if (!el) return;
    el.hidden = false;
    el.className = 'rf-ff-zip-result ' + (ok ? 'rf-ff-zip-result--ok' : 'rf-ff-zip-result--no');
    el.textContent = (ok ? '✓ ' : '✕ ') + msg;
    var rest = root.querySelector('[data-rf-delivery-rest]');
    if (rest) rest.hidden = !ok;
  }

  function clearZipResult() {
    var el = root.querySelector('[data-rf-zip-result]');
    if (el) { el.hidden = true; el.textContent = ''; }
    var rest = root.querySelector('[data-rf-delivery-rest]');
    if (rest) rest.hidden = true;
  }

  /* ---------- delivery dates ---------- */

  function buildDateTiles() {
    var wrap = root.querySelector('[data-rf-dates]');
    if (!wrap) return;
    var DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    var MONS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    var html = '';
    var d = new Date();
    d.setDate(d.getDate() + cfg.leadDays);

    for (var i = 0, added = 0; i < 40 && added < cfg.lookahead; i++) {
      var day = new Date(d.getTime() + i * 86400000);
      if (cfg.deliveryDays.indexOf(day.getDay()) === -1) continue;
      var iso = day.getFullYear() + '-' + pad(day.getMonth() + 1) + '-' + pad(day.getDate());
      var disp = DAYS[day.getDay()] + ' ' + MONS[day.getMonth()] + ' ' + day.getDate();
      html += '<button type="button" class="rf-ff-date" data-rf-date="' + iso + '" data-rf-date-display="' + disp + '" aria-pressed="false">' +
                '<span class="rf-ff-date__dow">' + DAYS[day.getDay()] + '</span>' +
                '<span class="rf-ff-date__num">' + day.getDate() + '</span>' +
                '<span class="rf-ff-date__mon">' + MONS[day.getMonth()] + '</span>' +
              '</button>';
      added++;
    }
    wrap.innerHTML = html;

    wrap.querySelectorAll('[data-rf-date]').forEach(function (b) {
      b.addEventListener('click', function () {
        clearDateSelection();
        b.classList.add('rf-ff-date--selected');
        b.setAttribute('aria-pressed', 'true');
        state.date = { iso: b.dataset.rfDate, display: b.dataset.rfDateDisplay };
        syncProperties(); refresh();
      });
    });
  }

  function clearDateSelection() {
    root.querySelectorAll('.rf-ff-date--selected').forEach(function (b) {
      b.classList.remove('rf-ff-date--selected');
      b.setAttribute('aria-pressed', 'false');
    });
    state.date = null;
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function money(cents) { return '$' + (cents / 100).toFixed(2).replace(/\.00$/, ''); }

  /* ---------- stacking visibility ---------- */

  /** Hide the stacking checkbox on cord sizes where the fee outweighs the wood. */
  function applyStackingVisibility() {
    if (!cfg.hideStackingOn.length || !section) return;
    var panel = document.querySelector('.product-adder .adder-panel');
    if (!panel) return;
    var current = currentVariantTitle();
    var hide = current && cfg.hideStackingOn.indexOf(current) !== -1;
    var host = panel.closest('.product-adder') || panel;
    host.hidden = Boolean(hide);
    if (hide && panel.classList.contains('adder-panel--checked')) panel.click();
  }

  function currentVariantTitle() {
    if (!section) return null;
    var checked = section.querySelector('variant-picker input[type="radio"]:checked');
    return checked ? (checked.value || '').trim() : null;
  }

  /* ---------- line item properties ---------- */

  function getForm() {
    return section ? section.querySelector('product-form-component form') : null;
  }

  function syncProperties() {
    var form = getForm();
    if (!form) return;
    form.querySelectorAll('.rf-ff-prop').forEach(function (el) { el.remove(); });
    if (!state.method) return;

    add(form, 'properties[Fulfillment]', state.method === 'pickup' ? 'Pickup at the market' : 'Local delivery');

    if (state.method === 'delivery') {
      if (state.address) add(form, 'properties[Delivery Address]', state.address);
      if (state.zip) add(form, 'properties[Delivery ZIP]', state.zip);
      if (state.date) add(form, 'properties[Delivery Date]', state.date.display);
      if (state.band) add(form, 'properties[Delivery Zone]', state.band);
    }
  }

  function add(form, name, value) {
    var i = document.createElement('input');
    i.type = 'hidden'; i.name = name; i.value = value; i.className = 'rf-ff-prop';
    form.appendChild(i);
  }

  /* ---------- gating + state ---------- */

  function complete() {
    if (!state.method) return false;
    if (state.method === 'pickup') return true;
    return Boolean(state.band && state.deliveryVariantId && state.address.trim() && state.date);
  }

  function message() {
    if (!state.method) return 'Choose pickup or delivery to continue';
    if (state.method === 'pickup') return '';
    if (!state.band) return 'Enter your ZIP code to check delivery';
    if (!state.address.trim()) return 'Add your delivery address to continue';
    if (!state.date) return 'Pick a delivery day to continue';
    return '';
  }

  function refresh() {
    var addr = root.querySelector('[data-rf-address]');
    if (addr) state.address = addr.value || '';

    var msgEl = root.querySelector('[data-rf-message]');
    var sumEl = root.querySelector('[data-rf-summary]');
    var ok = complete();

    if (ok) {
      var parts = state.method === 'pickup'
        ? ['Pickup at the market']
        : ['Delivery ' + state.date.display, state.address.trim(), money(state.deliveryPrice) + ' delivery'];
      if (sumEl) { sumEl.textContent = parts.join(' · '); sumEl.hidden = false; }
      if (msgEl) msgEl.hidden = true;
    } else {
      if (sumEl) sumEl.hidden = true;
      if (msgEl) { msgEl.textContent = message(); msgEl.hidden = false; }
    }
    lockAtc(!ok);
  }

  function lockAtc(locked) {
    if (!section) return;
    section.classList.toggle('rf-ff--locked', locked);
    section.querySelectorAll('[ref="addToCartButton"], .add-to-cart-button, [name="add"]').forEach(function (b) {
      if (locked) {
        b.dataset.rfFfLocked = 'true';
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
      } else if (b.dataset.rfFfLocked === 'true') {
        delete b.dataset.rfFfLocked;
        b.disabled = !state.variantAvailable;
        if (state.variantAvailable) b.removeAttribute('aria-disabled');
      }
    });
    section.querySelectorAll('.accelerated-checkout-block').forEach(function (c) {
      c.hidden = true; c.setAttribute('aria-hidden', 'true');
    });
  }

  function interceptSubmit() {
    if (!section) return;
    var a = root.querySelector('[data-rf-address]');
    if (a) a.addEventListener('input', function () { state.address = a.value; syncProperties(); refresh(); });

    section.addEventListener('submit', function (e) {
      if (!e.target.matches('product-form-component form')) return;
      if (!complete()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        refresh();
        return;
      }
      syncProperties();
    }, true);
  }

  /* ---------- add the delivery fee after main add ---------- */

  function bindCartAdd() {
    if (!section) return;
    var fc = section.querySelector('product-form-component');
    if (!fc) return;
    var busy = false;

    fc.addEventListener('cart:update', function (e) {
      if (busy) return;
      if (state.method !== 'delivery' || !state.deliveryVariantId) return;
      if (!e.detail || !e.detail.data) return;
      if (e.detail.data.source !== 'product-form-component') return;
      if (e.detail.data.didError) return;

      busy = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(state.deliveryVariantId, 10),
            quantity: 1,
            properties: {
              'Delivery Address': state.address.trim(),
              'Delivery Date': state.date ? state.date.display : '',
              'Delivery ZIP': state.zip || ''
            }
          }]
        })
      })
        .then(function (r) { return r.json(); })
        .then(function () { document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {} })); })
        .catch(function (err) { console.error('[RFFirewoodFulfillment] delivery fee add failed:', err); })
        .finally(function () { busy = false; });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init: init };
})();
